import { TokenHistory } from '@/clients/types';
import { endOfMonth } from 'date-fns';
import { logger } from './logger';

export type TokenUsageMap = Record<
  string,
  Record<string, { input_tokens: number; output_tokens: number }>
>;

function addTokenUsage(a: TokenUsageMap, b: TokenUsageMap): TokenUsageMap {
  const agg = copyTokenUsage(a);
  for (const [service, modelMap] of Object.entries(b)) {
    if (!service) {
      continue;
    }
    if (!agg[service]) {
      agg[service] = {};
    }
    for (const [model, tokenUsage] of Object.entries(modelMap)) {
      if (!model) {
        continue;
      }
      if (!agg[service][model]) {
        agg[service][model] = {
          input_tokens: 0,
          output_tokens: 0,
        };
      }
      agg[service][model].input_tokens += tokenUsage.input_tokens;
      agg[service][model].output_tokens += tokenUsage.output_tokens;
    }
  }
  return agg;
}

function copyTokenUsage(item: TokenUsageMap): TokenUsageMap {
  const copy: TokenUsageMap = {};
  for (const [service, modelMap] of Object.entries(item)) {
    if (!service) {
      continue;
    }
    if (!copy[service]) {
      copy[service] = {};
    }
    for (const [model, tokenUsage] of Object.entries(modelMap)) {
      if (!model) {
        continue;
      }
      copy[service][model] = Object.assign({}, tokenUsage);
    }
  }
  return copy;
}

const compactTokenFormat = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumSignificantDigits: 3,
});

// 1 万未満は桁区切り、それ以上は K / M などの単位付き（12.3K, 4.56M）
export function fmtTokenCount(v: number): string {
  return Math.abs(v) < 10000 ? v.toLocaleString('en-US') : compactTokenFormat.format(v);
}

// tokenHistory API を叩いて得られた結果を、グラフ描画用のデータに変換する
export function transformChartData(year: number, month: number, items: TokenHistory[]) {
  // Fill in the missing days
  const endDate =
    new Date().getMonth() + 1 === month ? new Date() : endOfMonth(new Date(year, month - 1));
  const daysInMonth = Array.from({ length: endDate.getDate() }, (_, i) => ({
    startTime: new Date(year, month - 1, i + 1).toISOString(),
    endTime: new Date(year, month - 1, i + 2).toISOString(),
    billSpent: 0,
    usage: {} as TokenUsageMap,
  }));
  items.forEach((item) => {
    const num = new Date(item.start_time).getDate() - 1;
    daysInMonth[num] = {
      startTime: item.start_time,
      endTime: item.end_time,
      billSpent: item.bill_spent,
      usage: item.usage,
    };
  });

  // Sum up the usage
  let billSpentSum = 0;
  let tokenUsageSum: TokenUsageMap = {};
  const records = daysInMonth.map((item) => {
    billSpentSum += item.billSpent;
    tokenUsageSum = addTokenUsage(tokenUsageSum, item.usage);
    return {
      startTime: new Date(item.startTime).getTime(),
      endTime: new Date(item.endTime).getTime(),
      billSpent: item.billSpent,
      billSpentSum: billSpentSum,
      tokenUsageSum: tokenUsageSum,
    };
  });

  // Extract provided models
  const models: [string, string][] = Object.entries(tokenUsageSum).flatMap(
    ([service, modelMap]) => {
      return Object.keys(modelMap).map((model) => [service, model] as [string, string]);
    }
  );

  // トークン利用量のデータが入っていないdayにたいして、0埋めを行う
  // TODO: 前段で実施しているfill処理と統合できないか検討する
  records.forEach((record) => {
    models.forEach(([service, model]) => {
      if (!record.tokenUsageSum[service]) {
        record.tokenUsageSum[service] = {};
      }
      if (!record.tokenUsageSum[service][model]) {
        record.tokenUsageSum[service][model] = {
          input_tokens: 0,
          output_tokens: 0,
        };
      }
    });
  });

  logger.info('records', records);
  logger.info('models', models);
  return {
    records,
    models,
  };
}
